import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ErrorState } from "@common/index";
import { useShipToApi } from "@/hooks/apiHooks/useShipToApi";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { useProductFacetApi } from "@/hooks/apiHooks/useProductFacetApi";
import { categoriesAtom } from "@/store/categoryAtoms";
import { shipToLocationIdAtom } from "@/store/shipToAtoms";
import { ProductGrid } from "@customer/Browse/components/ProductGrid";
import { useClampPageToRange } from "@customer/_helpers/useClampPageToRange";
import type { GetAllProductsResponse } from "@models/product";
import { useSearchFilters } from "./useSearchFilters";
import { buildSearchUrl } from "./_helpers/buildSearchUrl";
import { SortFilterSidebar } from "./SortFilterSidebar";
import { SortFilterBar } from "./SortFilterBar";
import { SortSheet } from "./SortSheet";
import { FilterSheet } from "./FilterSheet";

// The desktop price rail is the first CONTINUOUS control wired to a network fetch: React maps
// <input type="range"> onChange to the DOM `input` event, which fires once per stop crossed, so
// one drag from "All" to "$5" rewrote the URL five times and used to issue five /products
// requests — five token acquisitions and five skeleton flashes mid-drag. Holding an arrow key
// repeats at the OS rate, one request each.
//
// The FETCH is debounced, not the URL write. The URL stays the live source of truth, so the thumb
// tracks the pointer exactly and never lags behind the control the shopper is holding; only the
// request waits. Discrete clicks (chips, sort, in-stock) pay the same 250 ms, which is below the
// threshold where a results update reads as delayed and buys them the same no-flash behaviour.
const FILTER_FETCH_DEBOUNCE_MS = 250;

const EMPTY_RESPONSE: GetAllProductsResponse = {
    pageNumber: 1,
    totalPages: 0,
    count: 0,
    hasPreviousPage: false,
    hasNextPage: false,
    items: [],
};

interface SearchResultsProps {
    /**
     * The vendor page (#425): the vendor its route fixes. The grid is that
     * vendor's, headed by its name: no search term, any ?vendor= in the URL
     * ignored (as the category page ignores ?category=), and no Vendor chips.
     */
    fixedVendor?: { id: number; name: string };
}

// The query builder lives in _helpers/buildSearchUrl since #401 B4, when the
// category page moved onto it too.
export const SearchResults = ({ fixedVendor }: SearchResultsProps = {}) => {
    const { callShipToApi } = useShipToApi();
    const { getCategories } = useCategoryApi();
    const { getProductFacets } = useProductFacetApi();
    const categories = useAtomValue(categoriesAtom);
    const [searchParams, setSearchParams] = useSearchParams();
    const q = fixedVendor ? "" : (searchParams.get("q") ?? "").trim();
    const pageNumber = Math.max(1, Number(searchParams.get("page")) || 1);

    const urlFilters = useSearchFilters();
    const filters = fixedVendor
        ? { ...urlFilters, vendorIds: [fixedVendor.id] }
        : urlFilters;
    // The grid always runs (PO ruling 2026-10-05, #249): with no term the page
    // is the whole catalogue, "All products", narrowed by whatever filters the
    // URL carries — a vendor promotion with no slug links to
    // /c/search?vendor=<id> (the vendor page's fallback, #425), and
    // removing the last filter widens the grid rather than emptying the page.
    // buildSearchUrl omits the search parameters entirely when q is empty.

    // useSearchFilters memoizes the parsed filters (and therefore its arrays) on
    // `searchParams`, but the object it RETURNS is a fresh literal every render —
    // so `filters` itself can never be an effect dependency; the fetch would
    // re-run forever. The built URL is a plain string that only changes when a
    // filter value actually changes, so it is the stable key everything below
    // hangs off.
    const searchUrl = buildSearchUrl(q, pageNumber, filters);

    const [productPage, setProductPage] =
        useState<GetAllProductsResponse>(EMPTY_RESPONSE);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Page-local, not an app-scoped atom (#407): one outlives the page and is
    // cleared only when the next read starts, after the first paint, so a
    // revisit painted the previous visit's failure. The read is debounced after
    // the first fetch, so useMountRead (which issues immediately) does not fit.
    const [hasError, setHasError] = useState(false);

    // Lazy-load categories so the sidebar / FilterSheet chips render even if
    // the user landed here directly without opening the SearchPanel first.
    useEffect(() => {
        if (categories.length > 0) return;
        getCategories().catch(() => {
            /* notification layer surfaces errors */
        });
    }, [categories.length, getCategories]);

    // Brand + vendor chip values (#226). Fetched once per DESTINATION per
    // mount — the facets are computed over the catalogue the ship-to location
    // can receive, so a location change re-reads them with the grid, or the
    // chips keep describing the old destination. The ref — not the atom — is the guard: gating on "facets
    // are empty" would re-fetch forever for a catalogue that genuinely has no
    // brands.
    const shipToLocationId = useAtomValue(shipToLocationIdAtom);
    const facetsKey = shipToLocationId ?? "";
    const facetsRequestedForRef = useRef<string | null>(null);

    // NO CLEANUP, deliberately, and this is the fix for #226 review F1 — do not add one back.
    // A cleanup here can only cancel the read; it cannot re-issue it, because the re-run that
    // follows early-returns on the still-latched ref above. The effect re-runs on every StrictMode
    // double-mount (see main.tsx), on every change to the products request (searchUrl: the term,
    // a filter, the sort or the page), and whenever getProductFacets' identity
    // settles behind MSAL — so a cancel-and-don't-reissue cleanup killed the one read there was
    // ever going to be, and the facet feature with it. Ordering between a stale read and a live
    // one is now the hook's job (latest-wins), where a suppressed write always has a successor.
    useEffect(() => {
        if (facetsRequestedForRef.current === facetsKey) return;
        facetsRequestedForRef.current = facetsKey;
        getProductFacets().catch(() => {
            // Secondary read — getProductFacets re-throws and suppresses its own
            // toast. Swallow it here: the grid, the category chips and every
            // other filter still work, and FilterPanel simply hides the Brand
            // and Vendor sections while the lists are empty.
            //
            // Release the guard so the next run can try again. The effect is
            // keyed on the products request (searchUrl), so "the next run" is
            // the shopper's next search term, chip, sort or page — on a
            // term-less grid as much as on a search. Without this reset a
            // single failure would latch the guard and cost them both chip
            // lists (and, since #226's review, the In-Stock toggle) for the
            // rest of the mount, with no way back short of a page reload.
            facetsRequestedForRef.current = null;
        });
    }, [searchUrl, facetsKey, getProductFacets]);

    // A destination change re-answers the whole result set, so ?page is reset
    // exactly as every filter write resets it — a page kept from the previous
    // destination is as likely out of range as one kept across a narrowing.
    // Replace, not push: the change was not a navigation of the shopper's.
    const previousShipToRef = useRef(shipToLocationId);
    useEffect(() => {
        if (previousShipToRef.current === shipToLocationId) return;
        previousShipToRef.current = shipToLocationId;
        if (!searchParams.has("page")) return;
        const next = new URLSearchParams(searchParams);
        next.delete("page");
        setSearchParams(next, { replace: true });
    }, [shipToLocationId, searchParams, setSearchParams]);

    // Only the LATEST products request may set the page's state — a Retry
    // included (PR 373 review 1). Every fetch takes a ticket, and the effect's
    // cleanup retires the one in flight when the request changes, so a Retry
    // answering after the next filter's read can neither put the old results
    // under the new filter nor its failure over the new results.
    const latestFetch = useRef(0);
    const fetchProducts = useCallback(async () => {
        const ticket = ++latestFetch.current;
        const isLatest = () => ticket === latestFetch.current;
        setIsLoading(true);
        setHasError(false);
        try {
            const response =
                await callShipToApi<GetAllProductsResponse>(searchUrl);
            if (isLatest()) setProductPage(response);
        } catch {
            // useApi already surfaces the toast; flag the inline error too.
            if (isLatest()) setHasError(true);
        } finally {
            if (isLatest()) setIsLoading(false);
        }
    }, [searchUrl, callShipToApi]);

    // The first fetch of a mount is issued immediately — nothing can supersede it, and making the
    // first results of every search 250 ms later to smooth a drag is the wrong trade. Every fetch
    // after that is debounced; see FILTER_FETCH_DEBOUNCE_MS.
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchProducts();
            return () => {
                latestFetch.current += 1;
            };
        }
        const timer = setTimeout(() => fetchProducts(), FILTER_FETCH_DEBOUNCE_MS);
        return () => {
            latestFetch.current += 1;
            clearTimeout(timer);
        };
    }, [fetchProducts]);

    const isClampPending = useClampPageToRange(
        pageNumber,
        productPage,
        isLoading,
    );

    const setPage = useCallback(
        (newPage: number) => {
            const next = new URLSearchParams(searchParams);
            if (newPage <= 1) {
                next.delete("page");
            } else {
                next.set("page", String(newPage));
            }
            setSearchParams(next, { replace: false });
            if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        },
        [searchParams, setSearchParams],
    );

    return (
        <div data-testid="search-results" className="bg-base-100">
            <div className="max-w-7xl mx-auto flex">
                <SortFilterSidebar showVendors={!fixedVendor} />
                <div className="flex-1 min-w-0 px-2 md:px-4">
                    <h1 className="text-lg md:text-2xl font-bold text-neutral pt-4 md:pt-6 px-2">
                        {fixedVendor ? (
                            fixedVendor.name
                        ) : q ? (
                            <>
                                Results:{" "}
                                <span className="font-bold text-neutral">
                                    {q}
                                </span>
                            </>
                        ) : (
                            "All products"
                        )}
                    </h1>
                    {hasError ? (
                        <ErrorState onRetry={() => fetchProducts()} />
                    ) : (
                        <ProductGrid
                            products={productPage.items}
                            pageNumber={pageNumber}
                            totalPages={productPage.totalPages}
                            isLoading={isLoading || isClampPending}
                            onPageChange={setPage}
                        />
                    )}
                </div>
            </div>
            <SortFilterBar />
            <SortSheet />
            <FilterSheet showVendors={!fixedVendor} />
        </div>
    );
};
